/**
 * Background services — clock, online/offline monitor, event count wiring,
 * source status wiring, keyboard shortcuts. Each returns a cleanup function.
 */

import { appStore } from '@/core/store';
import { getDeskById } from '@/config/desk-presets';

/** Load persisted theme — always returns 'dark' (light theme removed). */
export function loadPersistedTheme(): 'dark' {
  return 'dark';
}

/**
 * Load persisted desk preset from localStorage.
 * Returns the saved desk ID if valid, or null if first launch / no saved preference.
 */
export function loadPersistedDesk(): string | null {
  try {
    const saved = localStorage.getItem('sentinel-desk');
    if (saved && getDeskById(saved)) return saved;
  } catch { /* localStorage unavailable */ }
  return null;
}

/** Check if this is the first launch (no desk preference saved). */
export function isFirstLaunch(): boolean {
  try {
    return localStorage.getItem('sentinel-desk') === null;
  } catch { return true; }
}

/** Save desk preference to localStorage. */
export function persistDesk(deskId: string): void {
  try { localStorage.setItem('sentinel-desk', deskId); } catch { /* ignore */ }
}

/** Start the UTC clock tick. Returns cleanup function. */
const DAY_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTH_NAMES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

export function startClock(): () => void {
  const el = document.getElementById('utc-clock');
  if (!el) return () => {};

  function update(): void {
    const now = new Date();
    const day = DAY_NAMES[now.getUTCDay()];
    const date = String(now.getUTCDate()).padStart(2, '0');
    const month = MONTH_NAMES[now.getUTCMonth()];
    const year = now.getUTCFullYear();
    const h = String(now.getUTCHours()).padStart(2, '0');
    const m = String(now.getUTCMinutes()).padStart(2, '0');
    const s = String(now.getUTCSeconds()).padStart(2, '0');
    el!.textContent = `${day}, ${date} ${month} ${year} ${h}:${m}:${s} UTC`;
  }

  update();
  const id = setInterval(update, 1000);
  return () => clearInterval(id);
}

/** Monitor online/offline status. Returns cleanup function. */
export function startOnlineMonitor(): () => void {
  function update(): void {
    const online = navigator.onLine;
    const dot = document.getElementById('status-dot');
    if (dot) {
      dot.className = online ? 'status-dot online' : 'status-dot offline';
      dot.title = online ? 'Online' : 'Offline';
    }
  }

  window.addEventListener('online', update);
  window.addEventListener('offline', update);
  update();
  return () => {
    window.removeEventListener('online', update);
    window.removeEventListener('offline', update);
  };
}

/** Wire live event count display to store. Returns cleanup function. */
export function wireEventCount(): () => void {
  return appStore.subscribe(
    (s) => s.events.length,
    (count) => {
      const el = document.getElementById('event-count');
      if (el) el.textContent = `Events: ${count.toLocaleString()}`;
    },
  );
}

/** Wire source status display. Returns cleanup function. */
export function wireSourceStatus(): () => void {
  return appStore.subscribe(
    (s) => s.events.length,
    () => {
      const el = document.getElementById('source-status');
      if (!el) return;
      const sourceNames = new Set(appStore.getState().events.map(e => e.source));
      el.textContent = `Sources: ${sourceNames.size}`;
    },
  );
}

